import { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';

export default function CatalogSearch() {

    const columns = useMemo(
        () => [
          {
            accessorKey: '_source.domain',
            header: 'Vendor',
            size: 140,
            Cell: ({ cell }) => (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {cell.getValue()}
              </Typography>
            ),
          },
          {
            accessorKey: '_source.product_code',
            header: 'SKU',
            size: 110,
            Cell: ({ cell }) => (
              <Chip
                label={cell.getValue()}
                size="small"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  borderRadius: 1,
                  fontWeight: 500,
                  border: 1,
                  borderColor: 'divider',
                }}
              />
            ),
          },
          {
            accessorKey: '_source.product_description',
            header: 'Description',
            size: 400,
            Cell: ({ cell }) => (
              <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.85rem' }}>
                {cell.getValue()}
              </Typography>
            ),
          },
          {
            accessorKey: '_source.price',
            header: 'Price',
            size: 90,
            Cell: ({ cell }) => {
              const val = cell.getValue();
              return (
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {val != null ? `$${Number(val).toFixed(2)}` : '—'}
                </Typography>
              );
            },
          },
          {
            accessorKey: '_source.in_stock',
            header: 'In Stock',
            size: 100,
            Cell: ({ cell }) => {
              const value = cell.getValue();
              const inStock = value === true || value === 1 || value === 'true';
              return (
                <Chip
                  label={inStock ? 'In Stock' : 'Out of Stock'}
                  size="small"
                  color={inStock ? 'success' : 'error'}
                  sx={{ fontSize: '0.725rem' }}
                />
              );
            },
          },
          {
            accessorKey: '_source.check_date',
            header: 'Last Update',
            size: 140,
            Cell: ({ cell }) => (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {cell.getValue() || '—'}
              </Typography>
            ),
          },
          {
            accessorKey: '_source.product_maker',
            header: 'Mfg',
            size: 140,
            Cell: ({ cell }) => (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {cell.getValue() || '—'}
              </Typography>
            ),
          },
          {
            accessorKey: '_source.creation_date',
            header: 'Creation Date',
            size: 140,
            Cell: ({ cell }) => (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {cell.getValue() || '—'}
              </Typography>
            ),
          },
          {
            accessorKey: '_source.inactive_product',
            header: 'Inactive',
            size: 90,
            Cell: ({ cell }) => {
              const val = cell.getValue();
              return val ? (
                <Chip label="Inactive" size="small" sx={{ bgcolor: 'action.hover', color: 'text.secondary', fontSize: '0.7rem' }} />
              ) : null;
            },
          },
        ],
        [],
      );

    const [rowCount, setRowCount] = useState(0);
    const [data, setData] = useState([]);
    const [queryText, setQueryText] = useState('');
    // object identity changes on every submit, so re-searching the same text still fires
    const [submitted, setSubmitted] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 15,
      });

    const submitSearch = useCallback(async (query, offset, domain) => {
        setIsLoading(true);
        setHasError(false);
        try {
            let url = '/catalog?query=' + encodeURIComponent(query) + '&offset=' + offset;

            if(domain && domain.toString().length > 0) {
                url += "&domain=" + encodeURIComponent(domain);
            }

            const res = await apiClient.get(url);
            const skus = res.data;
            setData(skus?.hits?.hits || []);
            setRowCount(skus?.hits?.total?.value || 0);
        } catch (error) {
            console.error('Catalog search error:', error);
            setHasError(true);
            setData([]);
            setRowCount(0);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleSubmitSearch = useCallback(() => {
        setPagination((p) => ({ ...p, pageIndex: 0 }));
        setSubmitted({ query: queryText });
    }, [queryText]);

    // searches launched from the header box arrive as /catalog?q=...
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const urlQuery = searchParams.get('q');

    useEffect(() => {
        if (urlQuery) {
            setQueryText(urlQuery);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
            setSubmitted({ query: urlQuery });
        }
        // location.key changes on every navigation, so repeating a search re-runs it
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    useEffect(() => {
        if (submitted === null) return;
        submitSearch(submitted.query, pagination.pageIndex * pagination.pageSize);
    }, [submitted, pagination.pageIndex, pagination.pageSize, submitSearch]);

      const table = useMaterialReactTable({
        columns,
        data,
        muiPaginationProps: {
            rowsPerPageOptions: [15],
            showFirstButton: false,
            showLastButton: false,
        },
        rowCount,
        initialState: { pagination: { pageSize: 15, pageIndex: 0 } },
        onPaginationChange: setPagination,
        manualPagination: true,
        state: {
            pagination,
            isLoading,
        },
        muiTablePaperProps: {
          elevation: 0,
          sx: {
            bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
            borderRadius: 2,
            boxShadow: 'none',
          },
        },
        muiTableBodyRowProps: ({ row }) => ({
          sx: {
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          },
          onClick: () => {
            const domain = row.original?._source?.domain;
            const sku = row.original?._source?.product_code;
            if (domain && sku) {
              const url = "Sku?domain=" + encodeURIComponent(domain) + "&sku=" + encodeURIComponent(sku);
              window.open(url, "_blank");
            }
          }
        }),
      });

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Catalog Search
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Search and inspect products across all connected vendor feeds
                </Typography>
            </Box>

            {/* Search Input Bar */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <TextField
                    id="query"
                    placeholder="Search by SKU, maker, or product description..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={queryText}
                    onChange={(event) => setQueryText(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    onKeyUp={(event) => {
                      if (event.key === 'Enter') handleSubmitSearch();
                    }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitSearch}
                    sx={{ px: 3, height: 40, whiteSpace: 'nowrap' }}
                >
                    Search
                </Button>
            </Paper>

            {/* Data Grid Display */}
            {data.length > 0 || isLoading ? (
                <MaterialReactTable table={table} />
            ) : hasError ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main', mb: 1.5, opacity: 0.8 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                        Search Failed
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        We couldn't reach the catalog. Check your connection and try again.
                    </Typography>
                    <Button variant="outlined" onClick={() => setSubmitted((prev) => ({ query: prev.query }))}>
                        Retry
                    </Button>
                </Paper>
            ) : (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <SearchIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1.5, opacity: 0.5 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                        {submitted === null ? 'Search the Catalog' : 'No Products Found'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {submitted === null
                            ? 'Enter a SKU, maker, or keyword in the search bar above to get started.'
                            : 'Try entering a different SKU, maker, or keyword in the search bar above.'}
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}
